import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, CheckCircle2, X, FileText, Clock, ExternalLink, PenSquare } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const URGENCY_LABEL = {
  vencido: { text: "🚨 PRAZO VENCIDO", tone: "bg-black text-white" },
  "1h": { text: "🚨🚨 PRAZO IMINENTE (< 1h)", tone: "bg-black text-white" },
  "3h": { text: "🚨 PRAZO CRÍTICO (< 3h)", tone: "bg-rose-700 text-white" },
  "6h": { text: "🔴🔴 URGENTE (< 6h)", tone: "bg-rose-600 text-white" },
  "24h": { text: "⚠️ CRÍTICO (< 24h)", tone: "bg-rose-600 text-white" },
};

function fmtLeft(seconds) {
  if (seconds < 0) return "vencido";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h >= 24) return `${Math.floor(h/24)}d ${h % 24}h`;
  return `${h}h ${m}min`;
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("pt-BR", { day:"2-digit", month:"2-digit", year:"numeric", hour:"2-digit", minute:"2-digit" });
}

/**
 * Global popup — mostrado após login se houver prazos críticos <24h.
 * Polls /api/deadlines/critical periodicamente. Backend-driven (não depende de timer do browser).
 */
export default function DeadlineAlert() {
  const [items, setItems] = useState([]);
  const [current, setCurrent] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0); // re-render countdown
  const navigate = useNavigate();

  const load = async () => {
    try {
      const r = await api.get("/deadlines/critical");
      const list = r.data.deadlines || [];
      setItems(list);
      if (list.length === 0) setDismissed(false); // reset for next batch
    } catch (e) {
      // silent — user might not be authenticated on this path
    }
  };

  useEffect(() => {
    load();
    const poll = setInterval(load, 60_000); // re-poll every 60s
    const cd = setInterval(() => setTick(t => t + 1), 1000); // countdown clock
    return () => { clearInterval(poll); clearInterval(cd); };
  }, []);

  if (dismissed || items.length === 0) return null;
  if (current >= items.length) setCurrent(0);
  const dl = items[current];
  if (!dl) return null;

  const secondsLeft = Math.max(0, (dl.seconds_left || 0) - tick);
  const urgency = secondsLeft < 0 ? "vencido"
    : secondsLeft < 3600 ? "1h"
    : secondsLeft < 3*3600 ? "3h"
    : secondsLeft < 6*3600 ? "6h" : "24h";
  const urg = URGENCY_LABEL[urgency];

  const goToProcess = () => {
    setDismissed(true);
    navigate(`/prazos?highlight=${dl.id}`);
  };

  const createPiece = () => {
    setDismissed(true);
    const qp = new URLSearchParams();
    if (dl.process_number) qp.set("process_number", dl.process_number);
    if (dl.expedient_type) qp.set("expedient", dl.expedient_type);
    if (dl.required_action) qp.set("action", dl.required_action);
    qp.set("deadline_id", dl.id);
    if (dl.client_id) {
      navigate(`/clients/${dl.client_id}/new-document?${qp.toString()}`);
    } else {
      // sem cliente vinculado — envia para lista de clientes para o advogado escolher
      toast.info("Escolha o cliente vinculado a este prazo para criar a peça.");
      navigate(`/clients?deadline_id=${dl.id}&process_number=${encodeURIComponent(dl.process_number || "")}`);
    }
  };

  const yes = async () => {
    setBusy(true);
    try {
      await api.post(`/deadlines/${dl.id}/confirm-completion`);
      toast.success("Prazo marcado como cumprido");
      setItems(prev => prev.filter(x => x.id !== dl.id));
      if (items.length <= 1) setDismissed(true);
    } catch (e) {
      toast.error("Falha ao confirmar");
    } finally { setBusy(false); }
  };

  const no = async () => {
    setBusy(true);
    try {
      await api.post(`/deadlines/${dl.id}/postpone-alert`);
      toast.info("Lembrarei em 1 hora");
      setItems(prev => prev.filter(x => x.id !== dl.id));
      if (items.length <= 1) setDismissed(true);
    } catch (e) {
      toast.error("Falha ao adiar");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-rose-950/80 backdrop-blur-sm flex items-center justify-center p-4"
         data-testid="deadline-alert-modal">
      <div className="relative w-full max-w-2xl bg-gradient-to-br from-rose-700 to-red-900 rounded-3xl shadow-2xl border-4 border-white/20 overflow-hidden">
        {/* Hermes messenger holding the pocket watch — brand icon, upper right */}
        <img
          src="/hermes-deadline.png"
          alt=""
          aria-hidden="true"
          className="pointer-events-none select-none absolute top-10 right-2 w-32 md:w-40 opacity-95 drop-shadow-[0_10px_25px_rgba(0,0,0,0.55)] z-20"
          data-testid="deadline-alert-hermes"
        />
        {/* Header */}
        <div className={`relative z-10 px-6 py-3 ${urg.tone} text-center font-black text-sm uppercase tracking-widest`} data-testid="deadline-alert-urgency">
          {urg.text}
        </div>

        <div className="relative z-10 p-8 text-white">
          <div className="flex items-center gap-4 mb-6 pr-36 md:pr-40">
            <div className="w-20 h-20 rounded-full bg-white/10 border-4 border-white/40 flex items-center justify-center shrink-0">
              <AlertTriangle className="w-12 h-12 text-yellow-300"/>
            </div>
            <div>
              <div className="text-yellow-300 text-sm uppercase tracking-widest font-bold">Atenção, prazo processual!</div>
              <div className="text-white/70 text-xs">
                {items.length > 1 && `${current + 1} de ${items.length} prazos críticos · `}
                Fonte oficial: DJEN/CNJ
              </div>
            </div>
          </div>

          <div className="rounded-2xl bg-white/5 backdrop-blur border border-white/10 p-5 space-y-2">
            <Row label="Processo" value={dl.process_number || "—"} mono strong/>
            <Row label="Tribunal" value={`${dl.court || "—"}${dl.court_unit ? " · " + dl.court_unit : ""}`}/>
            <Row label="Expediente" value={dl.expedient_type || "—"}/>
            {dl.required_action && <Row label="Providência" value={dl.required_action}/>}
            <Row label="Publicação" value={dl.publication_date || "—"}/>
            <Row label="Prazo" value={`${dl.deadline_quantity} ${dl.deadline_unit}${dl.business_days ? " úteis" : " corridos"}`}/>
            <Row label="Vencimento" value={fmtDateTime(dl.deadline_at)} strong/>
          </div>

          <div className="mt-5 rounded-xl bg-yellow-400 text-black p-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Clock className="w-6 h-6"/>
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold">Tempo restante</div>
                <div className="text-2xl font-black" data-testid="deadline-alert-countdown">{fmtLeft(secondsLeft)}</div>
              </div>
            </div>
            <button onClick={goToProcess} className="text-black/70 hover:text-black text-sm font-semibold inline-flex items-center gap-1">
              <ExternalLink className="w-4 h-4"/> Ver detalhes
            </button>
          </div>

          <div className="mt-6 text-center">
            <div className="text-2xl font-black uppercase tracking-wide text-white" data-testid="deadline-alert-question">
              Já cumpriu o prazo, colega?
            </div>
          </div>

          <button
            onClick={createPiece}
            className="mt-5 w-full h-12 rounded-2xl bg-white text-rose-800 hover:bg-yellow-100 font-black uppercase tracking-wide text-sm inline-flex items-center justify-center gap-2 shadow-lg transition-all"
            data-testid="deadline-alert-create-piece"
          >
            <PenSquare className="w-5 h-5" /> Criar peça agora
          </button>

          <div className="mt-4 grid grid-cols-2 gap-3">
            <button
              onClick={no}
              disabled={busy}
              className="h-16 rounded-2xl bg-white/10 hover:bg-white/20 border-2 border-white/30 text-white font-black text-xl uppercase transition-all disabled:opacity-50"
              data-testid="deadline-alert-no"
            >
              ❌ Não
              <div className="text-[10px] font-normal text-white/70 mt-0.5">Lembrar em 1h</div>
            </button>
            <button
              onClick={yes}
              disabled={busy}
              className="h-16 rounded-2xl bg-emerald-500 hover:bg-emerald-400 text-white font-black text-xl uppercase shadow-lg shadow-emerald-500/30 transition-all disabled:opacity-50"
              data-testid="deadline-alert-yes"
            >
              ✅ Sim
              <div className="text-[10px] font-normal text-white/90 mt-0.5">Confirmar cumprimento</div>
            </button>
          </div>

          {items.length > 1 && (
            <div className="mt-4 flex items-center justify-center gap-2 text-xs text-white/70">
              {items.map((_, i) => (
                <button key={i} onClick={()=>setCurrent(i)}
                  className={`w-2 h-2 rounded-full ${i === current ? "bg-yellow-300 w-6" : "bg-white/30"}`}/>
              ))}
            </div>
          )}

          <button onClick={()=>{ setDismissed(true); toast.info("Reabrirei em 1 hora se ainda pendente"); }}
            className="mt-4 w-full h-9 rounded-lg text-white/70 hover:text-white text-xs inline-flex items-center justify-center gap-1"
            data-testid="deadline-alert-dismiss">
            <X className="w-3.5 h-3.5"/> Fechar (será reaberto)
          </button>
        </div>

        <div className="px-6 py-3 bg-black/40 text-center text-[10px] text-white/50 uppercase tracking-widest">
          "Sim" = confirmação interna de cumprimento (não substitui protocolo judicial)
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, mono, strong }) {
  return (
    <div className="flex items-start gap-3">
      <div className="text-[10px] uppercase tracking-widest text-white/60 w-24 pt-1 shrink-0">{label}</div>
      <div className={`text-sm ${mono ? "font-mono" : ""} ${strong ? "font-bold text-yellow-300" : "text-white"}`}>{value}</div>
    </div>
  );
}
